/**
 * ProspectMiniMap — Iter165
 *
 * Embedded 300px Google Maps pinning a single Smith Protective prospect.
 * Uses the same loader pattern as SmithMap.jsx so the JS API is fetched
 * once per page. Falls back to a friendly banner if the key is missing
 * or the prospect has no lat/lng.
 */
import { useEffect, useRef } from "react";
import { MapPin } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
              || process.env.REACT_APP_GOOGLE_MAPS_KEY
              || "";

const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0B1120" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0B1120" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#94A3B8" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1E293B" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0F172A" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#334155" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
];

let mapsPromise = null;
function loadMaps() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!MAPS_KEY) return Promise.reject(new Error("no-key"));
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&v=weekly&libraries=places`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => reject(new Error("Maps script failed"));
    document.head.appendChild(s);
  });
  return mapsPromise;
}

const TIER_COLOR = {
  hot: "#DC2626", high: "#16A34A", qualified: "#EAB308",
  nurture: "#3B82F6", low: "#94A3B8",
};

export default function ProspectMiniMap({ prospect }) {
  const el = useRef(null);
  const lat = prospect?.property?.lat;
  const lng = prospect?.property?.lng;

  useEffect(() => {
    if (!el.current || lat == null || lng == null) return;
    let cancelled = false;
    loadMaps().then((maps) => {
      if (cancelled) return;
      const map = new maps.Map(el.current, {
        center: { lat, lng },
        zoom: 15,
        styles: DARK_STYLE,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "cooperative",
      });
      const color = TIER_COLOR[prospect.tier?.tier] || "#94A3B8";
      new maps.Marker({
        position: { lat, lng }, map,
        title: prospect.company?.name,
        icon: {
          path: maps.SymbolPath.CIRCLE, scale: 12,
          fillColor: color, fillOpacity: 0.95,
          strokeWeight: 3, strokeColor: "white",
        },
      });
    }).catch(() => { /* fall back handled below */ });
    return () => { cancelled = true; };
  }, [lat, lng, prospect]);

  if (lat == null || lng == null) {
    return (
      <div data-testid="mini-map-missing"
           className="rounded-lg border border-white/5 bg-black/30 p-4 text-center text-[11.5px] text-slate-500 italic">
        <MapPin className="w-5 h-5 mx-auto mb-1 text-slate-600" />
        Sem coordenadas — rode &quot;Enrich OSM&quot; ou geocodifique o endereço para ver o mapa.
      </div>
    );
  }
  if (!MAPS_KEY) {
    return (
      <div data-testid="mini-map-no-key"
           className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-4 text-center text-[11.5px] text-amber-200">
        Configure <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> no frontend/.env
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-white/5"
         data-testid="prospect-mini-map"
         style={{ height: 220 }}>
      <div ref={el} className="w-full h-full" />
    </div>
  );
}
